<script>
    import { afterUpdate } from 'svelte';
    import Emoji from '../../Emoji.svelte';
    import StatusIcon from './StatusIcon.svelte';
    import Dropzone from 'svelte-file-dropzone';
    import get from 'lodash/get';
    import clsx from 'clsx';
    import translations from '../translations';

    // Utils
    import { getBorderRadius } from '$lib/global/helper/border-radius';

    export let placeholder = '';
    export let showEmoji = false;
    export let emoji = '';
    export let required = false;
    export let invalid = false;
    export let pristine = true;
    export let touched = false;
    export let isFirst = false;
    export let isLast = false;
    export let finalFormInput = {};
    export let onFileAccept;
    export let uploading = false;
    export let errorMessage = '';
    export let language = 'de';

    let showError = invalid && touched;
    let showSuccess = !invalid && touched && !pristine;
    let showCheckmark = false;
    let showLoading = uploading;
    let rejectReason = '';
    let dragHovered = false;
    let inputElementContainer;

    $: {
        // this will detect when the focusable element in the dropzone has been focused or blurred
        // like this we can use final form input's way of detecting if the input is touched
        if (inputElementContainer) {
            const inputElements =
                inputElementContainer.getElementsByClassName('inner-input-container');

            if (inputElements.length === 1) {
                inputElements[0].addEventListener('focus', finalFormInput.onFocus);
                inputElements[0].addEventListener('blur', finalFormInput.onBlur);
            }
        }
    }

    const rejectReasonMap = {
        'file-invalid-type': translations[language].validation.InvalidFile,
        'file-too-large': translations[language].validation.FileTooLarge,
    };

    let wrapperClasses, inputClasses, ctaClasses, dropzoneClasses;

    afterUpdate(() => {
        showError = invalid && touched;
        showSuccess = !invalid && !pristine;
        showCheckmark = !uploading;
        showLoading = uploading;
    });

    let fileName;

    const handleFileDrop = (event) => {
        const { acceptedFiles, fileRejections } = event.detail;
        dragHovered = false;

        if (acceptedFiles.length) {
            const file = acceptedFiles[0];
            fileName = get(file, 'name', '');

            // to resolve "required" validation
            finalFormInput.onChange(fileName);
            invalid = false;
            rejectReason = '';

            return onFileAccept(file);
        }

        if (fileRejections.length) {
            const rejection = fileRejections[0];
            fileName = get(rejection, 'file.name', '');
            rejectReason = get(rejection, 'errors[0].code', '');

            invalid = true;

            // This will mark the input as touched
            finalFormInput.onFocus();
            finalFormInput.onBlur();
        }
    };

    const handleDragEnter = () => {
        dragHovered = true;
    };

    const handleDragLeave = () => {
        dragHovered = false;
    };

    const borderRadius = getBorderRadius('fieldsWrapper');

    $: wrapperClasses = clsx(
        borderRadius,
        'wrapper flex items-center relative box-border overflow-hidden bg-white hover:bg-gray-50',
        {
            ['rounded-b-none']: !isLast,
            ['rounded-t-none']: !isFirst,
            ['border-b']: isFirst && isLast,
            ['bg-gray-50']: dragHovered,
            invalid: invalid,
        },
    );

    $: inputClasses = clsx(
        borderRadius,
        'input w-full min-h-[90px] p-2 cursor-pointer box-border transition-all align-middle border-0 border-l border-r border-t font-ui text-base',
        {
            ['h-[1px]']: !invalid || !showError,
            ['h-[117px]']: invalid && showError,
            ['rounded-b-none']: !isLast,
            ['rounded-t-none']: !isFirst,
            invalid: invalid,
        },
    );

    $: dropzoneClasses = clsx(
        'dropzone border-dashed border rounded-md flex items-center w-full h-full',
        {
            ['py-4 pr-4 pl-16']: showEmoji,
            ['p-4']: !showEmoji,
            ['pr-12']: invalid,
        },
    );

    $: ctaClasses = clsx('cta text-gray-900 text-sm truncate overflow-hidden', {
        invalid: invalid,
    });
</script>

<div class={wrapperClasses} bind:this={inputElementContainer}>
    {#if showEmoji}
        <u class="absolute left-6 top-7 flex h-[18px] select-none items-center no-underline">
            <Emoji {emoji} size="24px" />
        </u>
    {/if}

    <Dropzone
        on:drop={handleFileDrop}
        on:dragenter={handleDragEnter}
        on:dragleave={handleDragLeave}
        on:filedialogcancel={() => {
            // This will mark the input as touched
            finalFormInput.onFocus();
            finalFormInput.onBlur();
        }}
        disableDefaultStyles
        containerClasses="inner-input-container w-full focus:outline-none focus:bg-gray-50"
        maxSize={5000000}
        multiple={false}
        accept="image/png,image/jpeg,.pdf"
        disabled={uploading}
        {required}
    >
        <div class={inputClasses} class:invalid>
            <div class={dropzoneClasses} class:invalid class:noEmoji={!showEmoji}>
                <div class="w-full">
                    <p class={ctaClasses} class:invalid>{fileName ? fileName : placeholder}</p>
                    {#if rejectReason || (errorMessage && showError)}
                        <div class="my-2 text-xs font-normal leading-tight text-[#ef444a]">
                            {rejectReason ? rejectReasonMap[rejectReason] : errorMessage}
                        </div>
                    {/if}
                    <p class="mt-1 text-xs text-gray-400">
                        ({!required ? 'optional, ' : ''}max. 5MB, .pdf, .jpg, .png)
                    </p>
                </div>
                <StatusIcon {showCheckmark} {showError} {showSuccess} {showLoading} />
            </div>
        </div>
    </Dropzone>
</div>

<style lang="scss">
    @import '../../variables';

    .wrapper {
        box-shadow: 0 1px 1px 0 rgba(#000, 0.1);
    }
</style>
